import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useRef, useState } from "react";

import { useTerminalSaveMutation } from "src/app/features/Terminal/services/server/TerminalApi";
import { keepTerminalProfileReadOnlyFlagsUtils, updateWatchedFieldsUtils } from "src/app/features/Terminal/TerminalProfileToTerminalUtils";
import type { TerminalEntity } from "src/app/features/Terminal/types/TerminalEntity";
import { useTerminalProfileLoadQuery } from "src/app/features/TerminalProfile/services/server/TerminalProfileApi";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useFormSubmitCompletion } from "../../../../../components/Form/hooks/useFormSubmitCompletion";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { InheritedFieldChangeConfirmationModal } from "../../../../../components/Modal/InheritedFieldChangeConfirmationModal";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import type { ID } from "../../../../../core/types/fe";
import { isTheSameByDeepCompare } from "../../../../../core/utils/utilFunctions";

export const TerminalCreateForm: FC<FormProps> = ({ runSubmit, formAction }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("terminal");

    const [formData, setFormData] = useState<TerminalEntity>({} as TerminalEntity);
    const completeSubmit = useFormSubmitCompletion(formAction, `${BASE_PATH}/${ResourceName.TERMINALS}`);
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useTerminalSaveMutation();
    const prevProfileIdRef = useRef<ID | undefined>(undefined);
    const pendingFormDataRef = useRef<TerminalEntity | null>(null);
    const formDataRef = useRef(formData);
    const [terminalProfileId, setTerminalProfileId] = useState<ID | undefined>(undefined);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    const applyUpdatedData = useCallback((updatedData: TerminalEntity) => {
        formDataRef.current = updatedData;

        if (updatedData.terminalProfileId !== prevProfileIdRef.current) {
            setTerminalProfileId(updatedData.terminalProfileId);
            setFormData(updatedData);

            if (updatedData.terminalProfileId === undefined) {
                prevProfileIdRef.current = undefined;
            }
        }
    }, []);

    // HANDLE ON CHANGE
    const onChangeCallback = useCallback(
        (newData: TerminalEntity) => {
            const updatedData = keepTerminalProfileReadOnlyFlagsUtils(newData, formDataRef.current);

            const hasProfileChange = updatedData.terminalProfileId !== prevProfileIdRef.current;
            if (hasProfileChange && updatedData.terminalProfileId !== undefined) {
                pendingFormDataRef.current = updatedData;
                setIsConfirmModalOpen(true);
                setFormData({ ...formDataRef.current });
                return;
            }

            applyUpdatedData(updatedData);
        },
        [applyUpdatedData],
    );

    const onConfirmProfileChange = useCallback(() => {
        const pendingData = pendingFormDataRef.current;
        pendingFormDataRef.current = null;
        setIsConfirmModalOpen(false);
        if (pendingData) {
            applyUpdatedData(pendingData);
        }
    }, [applyUpdatedData]);

    const onCancelProfileChange = useCallback(() => {
        pendingFormDataRef.current = null;
        setIsConfirmModalOpen(false);
        setFormData({ ...formDataRef.current });
    }, []);

    const { data: terminalProfileEntity } = useTerminalProfileLoadQuery(
        { entityId: terminalProfileId ? `${terminalProfileId}` : undefined },
        { skip: !terminalProfileId },
    );

    // HANDLE WATCHED FIELDS
    const updateWatchedFields = useCallback(() => {
        const currentProfileId = formDataRef.current?.terminalProfileId;

        if (!currentProfileId || !terminalProfileEntity || String(terminalProfileEntity.id) !== String(currentProfileId)) return null;

        prevProfileIdRef.current = currentProfileId;
        if (!formDataRef.current) return null;

        return updateWatchedFieldsUtils(terminalProfileEntity, formDataRef.current);
    }, [terminalProfileEntity]);

    useEffect(() => {
        if (!terminalProfileId || !terminalProfileEntity || String(terminalProfileEntity.id) !== String(terminalProfileId)) {
            return;
        }

        const prevProfileId = prevProfileIdRef.current;

        if (terminalProfileId !== prevProfileId) {
            let newFormData = updateWatchedFields();
            if (newFormData && !isTheSameByDeepCompare(formDataRef.current, newFormData)) {
                setFormData(newFormData);
                formDataRef.current = newFormData;
                newFormData = null;
            }
        }
    }, [terminalProfileId, updateWatchedFields, terminalProfileEntity]);

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            const currentFormData = formDataRef.current;
            if (!valid || !currentFormData) {
                return;
            }

            const result = await saveFormData({ data: currentFormData });

            const hasError = "error" in result;
            if (!hasError) {
                completeSubmit(
                    i18n.t("common.formSaveSuccess", { id: result.data.id, resource: "Terminal" }),
                    `${BASE_PATH}/${ResourceName.TERMINALS}/${FORM_ACTION.EDIT}/${result.data.id}`,
                );
            }
        },
        [saveFormData, completeSubmit],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<TerminalEntity>
                    name="TerminalCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
            <InheritedFieldChangeConfirmationModal
                open={isConfirmModalOpen}
                onConfirm={onConfirmProfileChange}
                onCancel={onCancelProfileChange}
            />
        </Spin>
    );
};
